import React, { useEffect, useState } from 'react';
import axiosInstance from '../../services/axiosInstance';
import {
  showSuccessToast,
  showErrorToast,
  showWarnToast,
} from '../../utils/toast';
import { useDispatch } from 'react-redux';
import { loginSuccess } from '../../store/authSlice';
import { authStorage } from '../../utils/authStorage';
import FileUploadField from '../CommonComponents/FileUploadField';
import { normalizePosType, POS_TYPE_OPTIONS, POS_TYPES } from '../../utils/posTypeModules';
import { fetchSubscriptionPackages } from '../../services/subscriptionPackageApi';

const formatLocalDateInput = (date) => {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const getCurrentMonthPeriod = () => {
  const now = new Date();
  return {
    start: formatLocalDateInput(new Date(now.getFullYear(), now.getMonth(), 1)),
    end: formatLocalDateInput(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
  };
};

const normalizeSubdomain = (value) =>
  String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 63);

const normalizeAdminUsername = (value) =>
  String(value || '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^A-Za-z0-9._-]/g, '')
    .slice(0, 50);

const getTenantBaseDomain = () => {
  const configured = String(process.env.REACT_APP_TENANT_BASE_DOMAIN || '').trim();
  if (configured) return configured;
  const host = window.location.hostname;
  const labels = host.split('.');
  if (labels.length >= 3) return labels.slice(-2).join('.');
  return 'zeroone-apps.com';
};

const AddCustomerModal = ({ editData, onClose, onUserAddedOrUpdated }) => {
  // Customer name and system type fields
  const [name, setName] = useState('');
  const [adminUsername, setAdminUsername] = useState('');
  const [adminUsernameTouched, setAdminUsernameTouched] = useState(false);
  const [adminEmail, setAdminEmail] = useState('');
  const [subdomain, setSubdomain] = useState('');
  const [subdomainTouched, setSubdomainTouched] = useState(false);
  const [systemType, setSystemType] = useState('');
  const [subscriptionPackageId, setSubscriptionPackageId] = useState('');
  const [subscriptionStartDate, setSubscriptionStartDate] = useState('');
  const [subscriptionEndDate, setSubscriptionEndDate] = useState('');
  const [subscriptionPackages, setSubscriptionPackages] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Logo file + preview (optional)
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const dispatch = useDispatch();

  useEffect(() => {
    let isMounted = true;

    const loadPackages = async () => {
      try {
        const response = await fetchSubscriptionPackages();
        const activePackages = (response.data?.data || []).filter((item) => item.status === 'ACTIVE');
        if (!isMounted) return;
        setSubscriptionPackages(activePackages);
        if (!editData && activePackages[0]?.id) {
          setSubscriptionPackageId(String(activePackages[0].id));
        }
      } catch (error) {
        console.error('Failed to load subscription packages:', error);
        showErrorToast('Failed to load subscription packages.');
      }
    };

    loadPackages();

    return () => {
      isMounted = false;
    };
  }, [editData]);

  // Prefill form if editing an existing customer
  useEffect(() => {
    const currentPeriod = getCurrentMonthPeriod();
    if (editData) {
      setName(editData.name ?? '');
      setAdminUsername(editData.admin_username || editData.username || editData.adminUsers?.[0]?.username || '');
      setAdminUsernameTouched(true);
      setAdminEmail(editData.email || editData.adminUsers?.[0]?.email || '');
      setSubdomain(editData.subdomain ?? '');
      setSubdomainTouched(Boolean(editData.subdomain));
      setSystemType(normalizePosType(editData.system_type));
      setSubscriptionPackageId(String(editData.subscription_package_id || editData.subscriptionPackage?.id || subscriptionPackages[0]?.id || ''));
      setSubscriptionStartDate((editData.subscription_start_date || '').slice(0, 10) || currentPeriod.start);
      setSubscriptionEndDate((editData.subscription_end_date || '').slice(0, 10) || currentPeriod.end);
      // If editing and logo exists, show it as preview (URL already normalized by backend)
      setLogoPreview(editData.logo || null);
    } else {
      setName('');
      setAdminUsername('');
      setAdminUsernameTouched(false);
      setAdminEmail('');
      setSubdomain('');
      setSubdomainTouched(false);
      setSystemType(POS_TYPES.RETAIL);
      setSubscriptionPackageId(subscriptionPackages[0]?.id ? String(subscriptionPackages[0].id) : '');
      setSubscriptionStartDate(currentPeriod.start);
      setSubscriptionEndDate(currentPeriod.end);
      setLogoFile(null);
      setLogoPreview(null);
    }
  }, [editData, subscriptionPackages]);

  useEffect(() => {
    if (editData || subdomainTouched) return;
    setSubdomain(normalizeSubdomain(name));
  }, [editData, name, subdomainTouched]);

  useEffect(() => {
    if (editData || adminUsernameTouched) return;
    setAdminUsername(normalizeAdminUsername(name));
  }, [adminUsernameTouched, editData, name]);

  const handleLogoChange = (file) => {
    setLogoFile(file);
    if (file) {
      const reader = new FileReader();
      reader.onload = (ev) => setLogoPreview(ev.target.result);
      reader.readAsDataURL(file);
    } else {
      // If cleared, keep existing preview only when editing and user didn't explicitly remove
      setLogoPreview(editData?.logo || null);
    }
  };

  const handleSubmit = async () => {
    const normalizedAdminUsername = adminUsername.trim();
    const normalizedAdminEmail = adminEmail.trim();

    if (!name || !normalizedAdminUsername || !normalizedAdminEmail || !subdomain || !systemType || !subscriptionPackageId || !subscriptionStartDate || !subscriptionEndDate) {
      showWarnToast('Please fill out all fields.');
      return;
    }

    if (!/^[A-Za-z0-9._-]{3,50}$/.test(normalizedAdminUsername)) {
      showWarnToast('Admin username must be 3-50 characters and may only use letters, numbers, dots, underscores, or hyphens.');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedAdminEmail)) {
      showWarnToast('Please enter a valid admin email address.');
      return;
    }

    if (!/^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/.test(subdomain)) {
      showWarnToast('Subdomain can only use lowercase letters, numbers, and hyphens.');
      return;
    }

    if (subscriptionEndDate < subscriptionStartDate) {
      showWarnToast('Subscription end date must be after the start date.');
      return;
    }

    setIsSubmitting(true);

    const method = editData ? 'put' : 'post';
    const url = editData
      ? `/superadmin/customers/${editData.id}`
      : `/superadmin/customers`;

    try {
      // Always use multipart so backend can accept optional file under field name `logo`
      const formData = new FormData();
      formData.append('name', name);
      formData.append('admin_username', normalizedAdminUsername);
      formData.append('email', normalizedAdminEmail);
      formData.append('subdomain', subdomain);
      formData.append('system_type', systemType);
      formData.append('status', 'ACTIVE');
      formData.append('subscription_package_id', subscriptionPackageId);
      formData.append('subscription_start_date', subscriptionStartDate);
      formData.append('subscription_end_date', subscriptionEndDate);
      if (logoFile) {
        formData.append('logo', logoFile);
      }

      const response = await axiosInstance.request({
        method,
        url,
        data: formData,
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      // Merge logo into customer object for localStorage and Redux
      let updatedCustomer;
      if (response.data?.customer) {
        updatedCustomer = response.data.customer;
        if (response.data.logo) updatedCustomer.logo = response.data.logo;
      } else {
        // If backend only returns logo and message, merge with existing customer
        const prevCustomer = JSON.parse(localStorage.getItem('customer')) || {};
        updatedCustomer = { ...prevCustomer };
        if (response.data.logo) updatedCustomer.logo = response.data.logo;
      }
      // Save to localStorage (scoped by customer_id)
      if (updatedCustomer.id) {
        // Use authStorage.setCustomerData to ensure only one customer_* key exists
        authStorage.setCustomerData(updatedCustomer.id, updatedCustomer);
      }
      // Update Redux auth.user if logged in as admin
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const userObj = JSON.parse(userStr);
        // Only update if user_type is admin (1) and customer_id matches
        if (userObj.user_type === 1 && userObj.customer_id === updatedCustomer.id) {
          const updatedUser = { ...userObj, logo: updatedCustomer.logo };
          localStorage.setItem('user', JSON.stringify(updatedUser));
          dispatch(loginSuccess({ token: localStorage.getItem('token'), user: updatedUser }));
        }
      }

      showSuccessToast(editData ? 'Customer updated successfully!' : 'Customer added successfully!');
      onUserAddedOrUpdated(updatedCustomer); // Pass updated customer if needed
      onClose(); // optionally close modal after success
    } catch (error) {
      console.error('Error saving customer:', error);
      const msg = error.response?.data?.message || error.response?.data?.error || 'Something went wrong while saving the customer.';
      showErrorToast(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/30 px-3 py-4 backdrop-blur-sm sm:px-4 sm:py-8">
      <div className="mx-auto flex min-h-full w-full max-w-md items-center">
        <div className="flex max-h-[calc(100vh-2rem)] w-full flex-col overflow-hidden rounded-2xl bg-white shadow-xl sm:max-h-[calc(100vh-4rem)]">

          {/* Modal title */}
          <div className="shrink-0 border-b border-slate-100 bg-white px-5 py-4 sm:px-6">
            <h2 className="text-xl font-semibold text-[#0f1e40]">
              {editData ? 'Edit Customer' : 'Add Customer'}
            </h2>
          </div>

          <div className="flex-1 overflow-y-auto px-5 py-4 sm:px-6">
            {/* Customer name input */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Customer Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2"
                placeholder="Enter customer name"
                disabled={isSubmitting}
                style={{
                  background: 'var(--color-bg)',
                  color: 'var(--color-primary)',
                  boxShadow: undefined
                }}
                onFocus={e => {
                  e.target.style.borderColor = '#000';
                  e.target.style.boxShadow = '0 0 0 2px #000';
                }}
                onBlur={e => {
                  e.target.style.borderColor = 'var(--color-border)';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Admin Username</label>
              <input
                type="text"
                autoComplete="username"
                value={adminUsername}
                onChange={(e) => {
                  setAdminUsernameTouched(true);
                  setAdminUsername(normalizeAdminUsername(e.target.value));
                }}
                className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2"
                placeholder="customer-admin"
                disabled={isSubmitting}
                style={{
                  background: 'var(--color-bg)',
                  color: 'var(--color-primary)',
                  boxShadow: undefined
                }}
                onFocus={e => {
                  e.target.style.borderColor = '#000';
                  e.target.style.boxShadow = '0 0 0 2px #000';
                }}
                onBlur={e => {
                  e.target.style.borderColor = 'var(--color-border)';
                  e.target.style.boxShadow = 'none';
                }}
              />
              <p className="mt-1 text-xs text-slate-500">
                Used with the admin email for customer admin login.
              </p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Admin Email</label>
              <input
                type="email"
                inputMode="email"
                autoComplete="email"
                value={adminEmail}
                onChange={(e) => setAdminEmail(e.target.value)}
                className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2"
                placeholder="customer.admin@example.com"
                disabled={isSubmitting}
                style={{
                  background: 'var(--color-bg)',
                  color: 'var(--color-primary)',
                  boxShadow: undefined
                }}
                onFocus={e => {
                  e.target.style.borderColor = '#000';
                  e.target.style.boxShadow = '0 0 0 2px #000';
                }}
                onBlur={e => {
                  e.target.style.borderColor = 'var(--color-border)';
                  e.target.style.boxShadow = 'none';
                }}
              />
              <p className="mt-1 text-xs text-slate-500">
                Used for the customer admin login, billing receipts, and password reset emails.
              </p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Workspace Subdomain</label>
              <div className="flex rounded-md border bg-white focus-within:ring-2 focus-within:ring-slate-300">
                <input
                  type="text"
                  value={subdomain}
                  onChange={(e) => {
                    setSubdomainTouched(true);
                    setSubdomain(normalizeSubdomain(e.target.value));
                  }}
                  className="min-w-0 flex-1 rounded-l-md px-4 py-2 text-sm text-slate-900 outline-none"
                  placeholder="minispot"
                  disabled={isSubmitting}
                />
                <span className="hidden shrink-0 items-center rounded-r-md bg-slate-50 px-3 text-xs font-semibold text-slate-500 sm:flex">
                  .{getTenantBaseDomain()}
                </span>
              </div>
              <p className="mt-1 break-all text-xs text-slate-500">
                Login URL: https://{subdomain || 'customer'}.{getTenantBaseDomain()}
              </p>
              {editData ? (
                <p className="mt-1 text-xs text-amber-600">
                  Changing this will change the customer's login URL.
                </p>
              ) : null}
            </div>

            {/* POS Type dropdown */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">POS Type</label>
              <select
                value={systemType}
                onChange={(e) => setSystemType(e.target.value)}
                className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2"
                disabled={isSubmitting}
                style={{
                  background: '#fff',
                  color: '#222',
                  boxShadow: undefined
                }}
                onFocus={e => {
                  e.target.style.borderColor = '#000';
                  e.target.style.boxShadow = '0 0 0 2px #888';
                }}
                onBlur={e => {
                  e.target.style.borderColor = 'var(--color-border)';
                  e.target.style.boxShadow = 'none';
                }}
              >
                <option value="">Select POS type</option>
                {POS_TYPE_OPTIONS.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            {/* Subscription package dropdown */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Subscription Package</label>
              <select
                value={subscriptionPackageId}
                onChange={(e) => setSubscriptionPackageId(e.target.value)}
                className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2"
                disabled={isSubmitting}
                style={{
                  background: '#fff',
                  color: '#222',
                  boxShadow: undefined
                }}
                onFocus={e => {
                  e.target.style.borderColor = '#000';
                  e.target.style.boxShadow = '0 0 0 2px #888';
                }}
                onBlur={e => {
                  e.target.style.borderColor = 'var(--color-border)';
                  e.target.style.boxShadow = 'none';
                }}
              >
                <option value="">Select subscription package</option>
                {subscriptionPackages.map((subscriptionPackage) => (
                  <option key={subscriptionPackage.id} value={subscriptionPackage.id}>
                    {subscriptionPackage.code} - ₱{Number(subscriptionPackage.base_monthly_amount || 0).toLocaleString()}/mo, ₱{Number(subscriptionPackage.additional_user_monthly_amount || 0).toLocaleString()}/user
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Subscription Start</label>
                <input
                  type="date"
                  value={subscriptionStartDate}
                  onChange={(e) => setSubscriptionStartDate(e.target.value)}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2"
                  disabled={isSubmitting}
                  style={{
                    background: '#fff',
                    color: '#222',
                    borderColor: 'var(--color-border)',
                  }}
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Subscription End</label>
                <input
                  type="date"
                  value={subscriptionEndDate}
                  min={subscriptionStartDate}
                  onChange={(e) => setSubscriptionEndDate(e.target.value)}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2"
                  disabled={isSubmitting}
                  style={{
                    background: '#fff',
                    color: '#222',
                    borderColor: 'var(--color-border)',
                  }}
                />
              </div>
            </div>

            {/* Logo upload (optional) */}
            <div className="mb-1">
              <FileUploadField
                label="Logo"
                accept="image/*"
                value={logoFile}
                preview={logoPreview}
                onChange={handleLogoChange}
                onRemove={() => { setLogoFile(null); setLogoPreview(null); }}
                optional={true}
                disabled={isSubmitting}
              />
            </div>
          </div>

          {/* Action buttons */}
          <div className="shrink-0 border-t border-slate-100 bg-white px-5 py-4 sm:px-6">
            <div className="flex justify-end gap-3">
              <button
                onClick={onClose}
                disabled={isSubmitting}
                className="h-[40px] min-w-[104px] rounded-xl border border-[#0f1e40] bg-white px-4 text-sm font-medium text-[#0f1e40] transition duration-150 focus:outline-none focus:ring-2 focus:ring-[#0f1e40] disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className={`h-[40px] min-w-[104px] rounded-xl px-4 text-sm font-medium text-white transition disabled:cursor-not-allowed ${isSubmitting ? 'bg-gray-400' : 'btn-primary'}
                  `}
              >
                {editData ? 'Update' : 'Add'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddCustomerModal;
